import { z } from 'zod';
/**
 * Tier 2 of the routing ladder. Tier 1 is deterministic (local commands and the
 * regex-matched compact routes) and stays free and instant; tier 3 is the
 * ~5 300-token `standard` contract. This planner exists only to keep requests
 * out of tier 3: it reads the message alone — never the page — and names the
 * compact routes that together satisfy it.
 *
 * It is not given the document —its styles would make it expensive— but it IS
 * given a few lines about the selection (`selection`): what it is, whether it
 * is a list, and what each item says. Without them «agrega otro servicio de
 * 1600» had nothing to hold on to and ended up as a new section.
 */
/** Routes the planner may name. `standard` is absent on purpose: it is the thing being avoided. */
export const AI_PLAN_ROUTES = [
    'theme_colors', 'theme_fonts', 'text_style', 'button_style', 'card_style',
    'icon_style', 'brand_logo', 'icon_swap', 'section_background', 'chrome_style',
    'element_style', 'content_batch', 'image_replace', 'insert_block',
    /* Uno más en la lista seleccionada —otra tarjeta, otro plan, otro servicio—:
       el editor duplica el último y reescribe sus textos con lo pedido. */
    'add_item',
    /* Quitar o mover lo seleccionado, o el elemento `item` de su lista. Los
       resuelve el editor sin IA; quitar se confirma antes. */
    'remove_item', 'move_item',
    /* Lo pedido cambia CUÁNTOS elementos tiene una lista —un día con otro
       horario sale de «Lunes – Viernes», dos precios se juntan—: el editor
       agrega o quita filas hasta `count` y reescribe todos sus textos. */
    'list_rewrite',
    /* El aire de todo el sitio: uno de los estilos de diseño del panel. */
    'design_style',
    /* Dos lecturas razonables: una pregunta corta con opciones concretas. Va
       sola, nunca junto a otros pasos. */
    'clarify',
    /* Agregar a una lista que generan las ofertas del negocio: la tarjeta sale
       de una oferta, así que lo que hay que crear es la oferta. */
    'create_offer',
    /* Una pregunta que se contesta sin ver la página —qué sabe hacer el
       asistente, por qué no salió lo anterior—: `message` es la respuesta. Va
       sola. Las que necesitan la página siguen a la ruta estándar. */
    'answer',
    /* Algo que el negocio TIENE —un gato en adopción, una propiedad, un
       platillo— y que no es otra tarjeta de la lista seleccionada: una entrada
       de una colección del CMS. La crea el editor sin IA; si la colección no
       existe, la propone y espera a que se confirme. */
    'cms_entry',
];
/**
 * Los sitios del editor que una respuesta (`answer`) puede nombrar, y a los que
 * su botón lleva. Sin lista cerrada, la respuesta se inventaba la interfaz
 * («selecciona la lista de animales»). Los del raíl y los dos espacios de
 * arriba (Contenido y Páginas).
 */
export const AI_PLAN_PLACES = ['templates', 'catalog', 'blog', 'styles', 'images', 'files', 'layers', 'cms', 'pages'];
/**
 * A compound request is a handful of intents, not dozens. The cap bounds both
 * the fan-out of provider calls and the blast radius of a bad plan.
 */
export const AI_PLAN_MAX_STEPS = 5;
export const aiPlanRequestSchema = z.object({
    message: z.string().min(1).max(2000),
    /** Narrows what the planner may name: token routes need brand scope. */
    styleScope: z.enum(['brand', 'selection']).default('brand'),
    /** Whether the editor has a selection, so the planner can prefer local routes. */
    hasSelection: z.boolean().default(false),
    /** A few lines describing the selection; see `selectionOutline` in the editor. */
    selection: z.string().max(1500).optional(),
    /**
     * Lo pedido y lo contestado en el turno anterior, en una línea: «¿por qué no
     * pudiste?» o «ahora en azul» hablan de él. Los avisos van dentro, que es
     * donde está la causa («no hay fotos de eso»).
     */
    previous: z.string().max(500).optional(),
    /**
     * Las colecciones del CMS del sitio, en una línea: nombre y campos
     * («Gatos en adopción (Nombre, Edad, Foto)»). Sin ellas, «agrega a Michi»
     * no sabe que hay dónde ponerlo y acaba copiando la tarjeta seleccionada.
     */
    collections: z.string().max(600).optional(),
});
export const aiPlanStepSchema = z.object({
    route: z.enum(AI_PLAN_ROUTES),
    /**
     * The single intent this step covers, in the user's own words. Each compact
     * route re-reads this text, so paraphrasing it loses the nouns those routes
     * depend on.
     */
    message: z.string().min(1).max(500),
    /**
     * `add_item`: how many to add (the editor caps it at 6). `list_rewrite`: how
     * many items the list must have in the end.
     */
    count: z.number().int().min(1).max(12).optional(),
    /**
     * Which item of the selected list the step is about, 1-based, as the
     * selection outline numbers them. Absent means the selection itself.
     */
    item: z.number().int().min(1).max(50).optional(),
    /** Only for `move_item`. */
    direction: z.enum(['up', 'down', 'first', 'last']).optional(),
    /**
     * Only for `image_replace`: what the photo must show, as 1–3 English
     * keywords with the main noun first («mug coffee»). The library is tagged in
     * English, so «taza» never matched «mug»; the worker searches with this and,
     * when nothing has that noun, says so instead of offering unrelated photos.
     * Absent when the user names no subject («otra foto», «más luminosa»).
     */
    query: z.string().trim().min(2).max(60).optional(),
    /**
     * Only for `add_item`: the texts of each new item, one array per item, in
     * the order and number the selection outline shows for an item of the list.
     * The editor duplicates and writes them itself — no second call that loads
     * a contract and nine elements to write one sentence. Any mismatch falls
     * back to that call.
     */
    texts: z.array(z.array(z.string().trim().min(1).max(300)).min(1).max(4)).min(1).max(6).optional(),
    /** Only for `answer`: where it is done, so the editor can show a button that goes there. */
    place: z.enum(AI_PLAN_PLACES).optional(),
    /** Only for `clarify`: the answers the user can tap. */
    options: z.array(z.string().trim().min(1).max(80)).min(2).max(4).optional(),
    /** Only for `cms_entry`: the site's collection it goes in, or a new one's name. */
    collection: z.string().trim().min(1).max(80).optional(),
    /** Only for `cms_entry`: the entry's name, when the user said it. */
    title: z.string().trim().min(1).max(200).optional(),
    /** Only for `cms_entry`: the other data the user gave, by field name. */
    values: z.record(z.string().trim().max(80), z.string().trim().max(500)).optional(),
    /** Only for `cms_entry` with a new collection: the fields every entry would have. */
    fields: z.array(z.string().trim().min(1).max(60)).max(6).optional(),
});
/**
 * Unknown routes are dropped rather than rejected: a plan that is three-quarters
 * right is still far cheaper than falling back to `standard`, and an empty
 * `steps` is the documented signal to do exactly that.
 */
/** Un campo opcional en `null` es «no está»: el modelo a veces los escribe todos. */
function withoutNulls(step) {
    if (!step || typeof step !== 'object' || Array.isArray(step))
        return step;
    return Object.fromEntries(Object.entries(step).filter(([, value]) => value !== null));
}
/*
 * Tolerante con la forma: el modelo a veces contesta la lista suelta —`[...]`—
 * en vez de `{"steps":[...]}`. Rechazarla dejaba el plan vacío sin avisar, y la
 * petición caía a las rutas de palabras; la primera evaluación lo destapó.
 */
/**
 * Un paso casi bien no se tira entero. `texts` sin anidar —`["Plan", "1600€"]`
 * para un solo elemento— se envuelve; y si aun así no vale, se quita sólo
 * `texts`: el editor escribe entonces con su llamada de siempre. Antes un
 * `texts` mal formado borraba el `add_item` y la petición se quedaba sin plan.
 */
function parseStep(raw) {
    const step = withoutNulls(raw);
    if (!step || typeof step !== 'object' || Array.isArray(step))
        return aiPlanStepSchema.safeParse(step);
    const record = step;
    const texts = record.texts;
    const fixed = Array.isArray(texts) && texts.length > 0 && texts.every((text) => typeof text === 'string')
        ? { ...record, texts: [texts] }
        : record;
    const parsed = aiPlanStepSchema.safeParse(fixed);
    if (parsed.success)
        return parsed;
    /* Lo opcional que sobra se quita, de uno en uno, antes de tirar el paso:
       un `place` que no está en la lista no puede llevarse la respuesta. */
    let rest = fixed;
    for (const key of ['texts', 'place']) {
        if (!(key in rest))
            continue;
        const { [key]: _dropped, ...without } = rest;
        rest = without;
        const retry = aiPlanStepSchema.safeParse(rest);
        if (retry.success)
            return retry;
    }
    return parsed;
}
/* También `[{"steps":[...]}]`, el objeto dentro de una lista: se desenvuelve. */
function unwrapPlan(value) {
    if (!Array.isArray(value))
        return value;
    const [only] = value;
    if (value.length === 1 && only && typeof only === 'object' && Array.isArray(only.steps))
        return only;
    return { steps: value };
}
export const aiPlanResponseSchema = z.preprocess(unwrapPlan, z.object({
    steps: z.array(z.unknown()).max(20).transform((steps) => steps
        .map(parseStep)
        .filter((result) => result.success)
        .map((result) => result.data)
        .slice(0, AI_PLAN_MAX_STEPS)),
}));
/**
 * Cómo acabó una petición al asistente, para saber qué falla sin tener que
 * esperar a que alguien lo cuente.
 *
 * `path` es por dónde fue: un atajo local, el planificador, las reglas de
 * palabras, una ruta directa, o una respuesta sin cambios (pregunta con
 * opciones, oferta). El texto del mensaje sólo viaja cuando algo salió mal
 * —nada aplicado, un reintento, algo pedido que no quedó—: son las frases que
 * hay que llevar al set de `planGolden.ts`.
 */
export const aiEditorOutcomeSchema = z.object({
    path: z.enum(['local', 'planner', 'keywords', 'direct', 'clarify', 'offer', 'batch', 'answer']),
    routes: z.array(z.string().max(40)).max(10),
    applied: z.number().int().min(0).max(10_000),
    /** Se dejó para confirmar (borrar): no aplicó nada, pero no falló. */
    pending: z.boolean().default(false),
    retried: z.number().int().min(0).max(20).default(0),
    literalMissing: z.number().int().min(0).max(20).default(0),
    ms: z.number().int().min(0).max(600_000),
    styleScope: z.enum(['brand', 'selection']),
    hasSelection: z.boolean(),
    message: z.string().max(200).optional(),
});
//# sourceMappingURL=aiPlan.js.map